import { Check, Users, UserX } from 'lucide-react';

import { Combobox } from '@apps/web/components/ui/combobox';
import {
    CommandEmpty,
    CommandGroup,
    CommandItem,
    CommandSeparator,
} from '@apps/web/components/ui/command';
import { UNASSIGNED_CARE_COORDINATOR_ID } from '@apps/web/hooks/use_care_coordinator_filter';
import { cn } from '@apps/web/lib/utils';
import { type CareCoordinatorRead } from '@libraries/typescript/api_client';

type CareCoordinatorFilterProps = {
    readonly careCoordinators: Array<CareCoordinatorRead>;
    readonly isOpen: boolean;
    readonly onOpenChange: (open: boolean) => void;
    readonly searchValue: string;
    readonly onSearchValueChange: (value: string) => void;
    readonly displayValue: string | null;
    readonly onSelectCoordinator: (coordinatorId: string) => void;
    readonly onShowAll: () => void;
    readonly isCoordinatorSelected: (coordinatorId: string) => boolean;
    readonly isShowingAll: boolean;
    readonly formatCoordinatorName: (
        coordinator: CareCoordinatorRead,
    ) => string;
    readonly triggerClassName?: string;
};

export function CareCoordinatorFilter({
    careCoordinators,
    isOpen,
    onOpenChange,
    searchValue,
    onSearchValueChange,
    displayValue,
    onSelectCoordinator,
    onShowAll,
    isCoordinatorSelected,
    isShowingAll,
    formatCoordinatorName,
    triggerClassName,
}: CareCoordinatorFilterProps) {
    if (careCoordinators.length === 0) {
        return null;
    }

    return (
        <Combobox
            open={isOpen}
            onOpenChange={onOpenChange}
            displayValue={
                displayValue ? (
                    <span className="flex items-center gap-2">
                        <Users className="h-4 w-4 shrink-0" />
                        <span className="truncate">{displayValue}</span>
                    </span>
                ) : undefined
            }
            placeholder={
                <span className="flex items-center gap-2">
                    <Users className="h-4 w-4 shrink-0" />
                    <span>All Care Coordinators</span>
                </span>
            }
            searchValue={searchValue}
            onSearchValueChange={onSearchValueChange}
            searchPlaceholder="Search care coordinators..."
            triggerClassName={cn('w-[240px] h-8', triggerClassName)}
            fullWidth={false}
        >
            <CommandEmpty>No care coordinators found.</CommandEmpty>
            <CommandGroup>
                <CommandItem onSelect={onShowAll}>
                    <Check
                        className={cn(
                            'mr-2 h-4 w-4',
                            isShowingAll ? 'opacity-100' : 'opacity-0',
                        )}
                    />
                    All Care Coordinators
                </CommandItem>
            </CommandGroup>
            <CommandSeparator />
            <CommandGroup>
                <CommandItem
                    value="No Care Coordinator"
                    onSelect={() =>
                        onSelectCoordinator(UNASSIGNED_CARE_COORDINATOR_ID)
                    }
                >
                    <Check
                        className={cn(
                            'mr-2 h-4 w-4',
                            isCoordinatorSelected(
                                UNASSIGNED_CARE_COORDINATOR_ID,
                            )
                                ? 'opacity-100'
                                : 'opacity-0',
                        )}
                    />
                    <div className="flex items-center gap-2">
                        <UserX className="text-muted-foreground h-4 w-4" />
                        <span>No Care Coordinator</span>
                    </div>
                </CommandItem>
                {careCoordinators.map((coordinator) => {
                    const isSelected = isCoordinatorSelected(coordinator.id);
                    return (
                        <CommandItem
                            key={coordinator.id}
                            value={formatCoordinatorName(coordinator)}
                            onSelect={() => onSelectCoordinator(coordinator.id)}
                        >
                            <Check
                                className={cn(
                                    'mr-2 h-4 w-4',
                                    isSelected ? 'opacity-100' : 'opacity-0',
                                )}
                            />
                            <div className="flex flex-col">
                                <span>
                                    {formatCoordinatorName(coordinator)}
                                </span>
                                {coordinator.email && (
                                    <span className="text-muted-foreground text-xs">
                                        {coordinator.email}
                                    </span>
                                )}
                            </div>
                        </CommandItem>
                    );
                })}
            </CommandGroup>
        </Combobox>
    );
}
